import { CryptoApi } from 'matrix-js-sdk/src/crypto-api';
import { deriveRecoveryKeyFromPassphrase } from 'matrix-js-sdk/src/crypto-api/index';
import { MatrixClient, MatrixError } from 'matrix-js-sdk/src/matrix';

import { VerificationErrorEnum } from 'contexts/MatrixDeviceVerificationContext/utils';
import logger from 'utils/logging/faro';

import { cacheSecretStorageKey, withSecretStorageKeyCache } from './secretStorageCallbacks';

export interface UnlockSecretStorageForLoginOptions {
  /** Current Matrix user id, used to cross-sign the device and refresh device info after unlock. */
  userId?: string | null;
  /** Current Matrix device id, used to cross-sign this device after unlock. */
  deviceId?: string | null;
  /**
   * Skip key-backup restore when no backup exists on the server. Only the post-login flow sets this
   * (first-time setup, where there is nothing to restore). The login gate always restores.
   */
  skipKeyRestoreWhenNoBackup?: boolean;
  /** Invoked when the background key-backup restore finishes successfully. */
  onKeyRestoreComplete?: () => void;
  /** Invoked when the background key-backup restore fails. */
  onKeyRestoreError?: (error: unknown) => void;
}

/**
 * Bootstrap cross-signing, swallowing the interactive-auth 401 that Synapse returns when device
 * signing keys can be uploaded without UIA (the SDK retries with auth=null and we accept that).
 */
export const bootstrapCrossSigningWithAuth = async (crypto: CryptoApi): Promise<void> => {
  await crypto.bootstrapCrossSigning({
    authUploadDeviceSigningKeys: async (makeRequest: (auth: null) => Promise<void>) => {
      try {
        await makeRequest(null);
      } catch (authError) {
        if (authError instanceof MatrixError && authError.httpStatus === 401) return;
        throw authError;
      }
    },
  });
};

/**
 * Unlock secret storage with a security code and complete cross-signing restore.
 *
 * Pure async function shared by the post-login settings flow (`useSecretStorageSecurityCode`) and
 * the login-time gate (`LoginSecretStorageUnlockGate`). It derives the secret storage key from the
 * passphrase, caches it for the SDK's `getSecretStorageKey` callback, and runs the full
 * bootstrap/restore inside `withSecretStorageKeyCache` so the cached key is available for the
 * duration of the crypto operations and cleared afterwards.
 *
 * Unlike `useSecretStorageSecurityCode.unlockWithSecurityCode`, this takes the `MatrixClient`
 * directly, so it can be called with an in-flight client before `mxLoggedIn === true`.
 */
export const unlockSecretStorageForLogin = async (
  matrixClient: MatrixClient,
  securityCode: string,
  options: UnlockSecretStorageForLoginOptions = {}
): Promise<void> => {
  const { userId, deviceId, skipKeyRestoreWhenNoBackup = false, onKeyRestoreComplete, onKeyRestoreError } = options;

  await withSecretStorageKeyCache(async () => {
    const crypto = matrixClient.getCrypto();
    if (!crypto) throw new Error('Crypto not ready');

    const defaultKeyId = await matrixClient.secretStorage.getDefaultKeyId();
    if (!defaultKeyId) throw new Error('No secret storage configured');

    const keyTuple = await matrixClient.secretStorage.getKey(defaultKeyId);
    if (!keyTuple) throw new Error('Secret storage key not found');

    const [keyId, keyInfo] = keyTuple;
    if (!keyInfo.passphrase) throw new Error('Key was not created from a passphrase');

    const { salt, iterations } = keyInfo.passphrase;
    const privateKey = await deriveRecoveryKeyFromPassphrase(securityCode, salt, iterations);
    cacheSecretStorageKey(keyId, keyInfo, privateKey);

    // Ensure the user's own public cross-signing identity is in the local crypto store before
    // importing the private keys. bootstrapCrossSigning needs to find the public identity
    // locally — at the pre-sync login gate the client has not started syncing yet so the
    // identity has not been fetched automatically. Passing downloadUncached=true forces a
    // /keys/query that populates the local store. This was previously a side-effect of the
    // checkNeedsSecretStorageUnlock probe (which called userHasCrossSigningKeys); now it lives
    // here where the dependency is explicit. When called post-login (settings flow) the identity
    // is already present and the call is a cheap no-op.
    if (userId) await crypto.userHasCrossSigningKeys(userId, true).catch(() => null);

    // Race hardener: `userHasCrossSigningKeys` returns when /keys/query is dispatched, but the
    // SDK's internal queue may still be processing the response when bootstrapCrossSigning runs.
    // `getUserDeviceInfo([userId], true)` waits for the device list to be settled before import
    // is attempted. Best-effort — for poisoned accounts where the response never produces a valid
    // identity (NoSignatureFound) this still throws downstream and is mapped below.
    if (userId) await crypto.getUserDeviceInfo([userId], true).catch(() => null);

    try {
      await bootstrapCrossSigningWithAuth(crypto);
      await crypto.bootstrapSecretStorage({});
    } catch (decryptError) {
      if (decryptError instanceof Error && decryptError.message.includes('bad MAC')) {
        throw new Error(VerificationErrorEnum.INVALID_SECURITY_CODE);
      }
      // Poisoned account: server-side cross-signing identity is corrupted (e.g. NoSignatureFound
      // from /keys/query), so the SDK cannot construct a public identity locally and refuses to
      // import the private keys it just decrypted from secret storage. Re-throw as a dedicated
      // error so the unlock bus can map it to a reset-keys-actionable UI state instead of the
      // generic UNKNOWN_ERROR.
      if (
        decryptError instanceof Error &&
        (decryptError.message.includes('importCrossSigningKeys failed') ||
          decryptError.message.includes('No public identity found'))
      ) {
        throw new Error(VerificationErrorEnum.BROKEN_CROSS_SIGNING_IDENTITY);
      }
      throw decryptError;
    }

    if (userId && deviceId) {
      const status = await crypto.getDeviceVerificationStatus(userId, deviceId);
      if (!status?.crossSigningVerified) {
        await crypto.crossSignDevice(deviceId);
      }
    }

    if (userId) await crypto.getUserDeviceInfo([userId], true);

    // Skip key restore only when we are certain there is no existing backup (first-time setup).
    const backupInfoForRestore = await crypto.getKeyBackupInfo().catch(() => null);
    if (skipKeyRestoreWhenNoBackup && !backupInfoForRestore) return;

    await crypto.loadSessionBackupPrivateKeyFromSecretStorage();

    // No need to await; key backup restore continues in background.
    crypto
      .restoreKeyBackup()
      .then(({ total, imported }) => {
        logger.info(`Key backup: ${imported}/${total} keys restored`);
        onKeyRestoreComplete?.();
      })
      .catch((err) => {
        logger.error('Key backup restore failed', err);
        onKeyRestoreError?.(err);
      });
  });
};
